import { Box } from '@dtx-company/design-system/src'
import { FC, useEffect, useState } from 'react'
import { LoggedOutGeneratorSection } from '../../LoggedOutCodeGenerator.types'
import {
  selectProgress,
  selectScanDestinationPresent,
  selectSection
} from '@app/code/src/machines/locg/locgSelectors'
import { useGlobalServices } from '@app/common/src/hooks/useGlobalServices'
import { useProgressBarMobileSections } from './hooks/useProgressBarMobileSections'
import { useSelector } from '@xstate/react'

interface ProgressBarSegmentProps {
  active: boolean
  width: number
  displayBorder: boolean
  label?: string
}

const ProgressBarSegment: FC<ProgressBarSegmentProps> = props => {
  const borderKey = props.active ? 'borderLeft' : 'borderRight'

  return (
    <Box
      sx={{
        height: '100%',
        position: 'relative',
        flex: props.width,
        ':before, :after': {
          content: "''",
          position: 'absolute',
          inset: 0
        },
        ':after': {
          ...(props.displayBorder && {
            [`${borderKey}Width`]: 2,
            [`${borderKey}Style`]: 'solid',
            [`${borderKey}Color`]: props.active ? 'surface.0' : 'surface.4'
          })
        },
        ':before': {
          backgroundColor: 'surface.4',
          width: props.active ? '100%' : 0,
          transition: 'width 0.2s ease-in-out'
        }
      }}
    >
      {props.label && (
        <Box
          sx={{
            position: 'absolute',
            left: 5,
            zIndex: 1,
            top: 0,
            whiteSpace: 'nowrap'
          }}
        >
          <Box
            sx={theme => ({
              color: 'text.secondary',
              fontSize: theme.spacing(2.25),
              textTransform: 'uppercase',
              fontWeight: '700',
              margin: 0,
              lineHeight: 'normal'
            })}
            component={'p'}
            id={'progress-bar-mobile-label'}
          >
            {props.label}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export const ProgressBarMobile: FC = () => {
  const { locgService } = useGlobalServices()
  const section = useSelector(locgService, selectSection)
  const progress = useSelector(locgService, selectProgress)
  const [activeSectionIndex, setActiveSectionIndex] = useState(0)
  const sections = useProgressBarMobileSections()
  const activeSection = sections.find(el => el.id === section)
  const scanDestinationPresent = useSelector(locgService, selectScanDestinationPresent)

  // keep highest index when user scrolls to the top
  useEffect(() => {
    setActiveSectionIndex(_activeSectionIndex =>
      Math.max(
        sections.findIndex(el => el.id === section),
        _activeSectionIndex,
        0
      )
    )
  }, [section, sections])

  return (
    <Box
      sx={theme => ({
        display: {
          xs: 'flex',
          md: 'none'
        },
        height: theme.spacing(2.5),
        boxShadow: 2,
        mb: 4
      })}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={progress * 100}
      aria-describedby={'progress-bar-mobile-label'}
    >
      {sections.map((section, index) => {
        const displayLabel =
          section.id === activeSection?.id ||
          (section.groupId && section.groupId === activeSection?.groupId)
        const hasSectionBeenVisited = activeSectionIndex > index
        const active =
          hasSectionBeenVisited ||
          (activeSectionIndex >= index &&
            (section.id != LoggedOutGeneratorSection.ScanDestination || scanDestinationPresent))

        return (
          <ProgressBarSegment
            width={section.width}
            active={active}
            displayBorder={index === 0 ? !active : true}
            label={displayLabel ? section.label : ''}
            key={section.id}
          />
        )
      })}
    </Box>
  )
}
